import React, { useEffect, useCallback } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';
import { useChatStore } from '../hooks/useChatStore';
import { requestNotificationPermission, ensureNotificationsEnabled } from '../services/notifications';
import { isUnavailable, onUnavailableChange, resetConnectivity } from '../services/connectivity';
import ChatList from './ChatList/ChatList';
import ChatWindow from './Chat/ChatWindow';
import SettingsPanel from './Settings/SettingsPanel';
import AIAssistant from './AI/AIAssistant';
import ScheduledMessagesPanel from './Chat/ScheduledMessagesPanel';
import ChatRequestHandler from './Chat/ChatRequestHandler';
import OnboardingTour, { isOnboardingDone } from './Onboarding/OnboardingTour';
import '../styles/main.css';

export default function MainLayout() {
  const { user, logout, token } = useAuthStore();
  const { connectSocket, disconnectSocket } = useChatStore();
  const [showSettings, setShowSettings] = React.useState(false);
  const [showAI, setShowAI] = React.useState(false);
  const [showScheduled, setShowScheduled] = React.useState(false);
  const [theme, setTheme] = React.useState(user?.theme || 'light');
  const [activeAIChat, setActiveAIChat] = React.useState(false);
  const [noConnection, setNoConnection] = React.useState(false);
  const [showOnboarding, setShowOnboarding] = React.useState(() => !isOnboardingDone());

  useEffect(() => {
    let timer = null;
    const off = onUnavailableChange((v) => {
      if (v) {
        timer = setTimeout(() => setNoConnection(true), 4000);
      } else {
        if (timer) clearTimeout(timer);
        timer = null;
        setNoConnection(false);
      }
    });
    if (isUnavailable()) {
      timer = setTimeout(() => setNoConnection(true), 4000);
    }
    return () => {
      if (timer) clearTimeout(timer);
      off();
    };
  }, []);

  const handleUserGesture = useCallback(() => {
    requestNotificationPermission();
    document.removeEventListener('click', handleUserGesture);
    document.removeEventListener('keydown', handleUserGesture);
  }, []);

  useEffect(() => {
    if (token) {
      resetConnectivity();
      connectSocket(token);
      ensureNotificationsEnabled();
      document.addEventListener('click', handleUserGesture, { once: true });
      document.addEventListener('keydown', handleUserGesture, { once: true });
    }
    return () => {
      disconnectSocket();
      document.removeEventListener('click', handleUserGesture);
      document.removeEventListener('keydown', handleUserGesture);
    };
  }, [token]);

  useEffect(() => {
    if (user?.theme) {
      setTheme(user.theme);
    }
  }, [user?.theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'custom' && user) {
      if (user.bgColor) root.style.setProperty('--bg', user.bgColor);
      if (user.chatBgColor) root.style.setProperty('--chat-bg', user.chatBgColor);
      if (user.textColor) root.style.setProperty('--text', user.textColor);
      if (user.chatTextColor) root.style.setProperty('--chat-text', user.chatTextColor);
      if (user.msgBgColor) root.style.setProperty('--msg-bg', user.msgBgColor);
      if (user.textColor) root.style.setProperty('--primary', user.textColor);
      if (user.systemTextColor) root.style.setProperty('--system-text', user.systemTextColor);
      if (user.bgColor) root.style.setProperty('--hover-bg', user.bgColor);
    } else {
      root.style.removeProperty('--bg');
      root.style.removeProperty('--chat-bg');
      root.style.removeProperty('--text');
      root.style.removeProperty('--chat-text');
      root.style.removeProperty('--msg-bg');
      root.style.removeProperty('--primary');
      root.style.removeProperty('--system-text');
      root.style.removeProperty('--border');
      root.style.removeProperty('--hover-bg');
    }
  }, [theme, user]);

  return (
    <div className={`app-layout theme-${theme}${['dark', 'blue', 'purple', 'green'].includes(theme) ? ' theme-dark' : ''}`}>
      <ChatList
        onSettings={() => setShowSettings(true)}
        onAI={() => { setActiveAIChat(true); setShowAI(false); }}
        user={user}
        onLogout={logout}
        activeAIChat={activeAIChat}
        onOpenAIChat={() => setActiveAIChat(true)}
        onCloseAIChat={() => setActiveAIChat(false)}
        onOpenScheduled={() => setShowScheduled(true)}
      />
      <ChatWindow activeAIChat={activeAIChat} onOpenAI={() => setActiveAIChat(true)} />
      <ChatRequestHandler />
      {showScheduled && <ScheduledMessagesPanel onClose={() => setShowScheduled(false)} />}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} user={user} onThemeChange={setTheme} />}
      {showOnboarding && <OnboardingTour onFinish={() => setShowOnboarding(false)} />}
      {noConnection && (
        <div className="no-connection-overlay">
          <img src="/no_call.jpeg" alt="" className="no-connection-image" />
          <p className="no-connection-text">Нет соединения с сервером.<br />Проверьте подключение к интернету</p>
          <div className="no-connection-actions">
            <button className="no-connection-btn primary" onClick={() => window.location.reload()}>Попробовать снова</button>
            <button className="no-connection-btn" onClick={() => setNoConnection(false)}>Остаться в приложении</button>
          </div>
        </div>
      )}
    </div>
  );
}
